import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { When } from 'react-if';

import { Box } from '@nerdwallet/currency/components/Box';
import { CenteredContainer } from '@nerdwallet/currency/components/CenteredContainer';
import { Link } from '@nerdwallet/currency/components/Link';
import { Text } from '@nerdwallet/currency/components/Text';
import { Button } from '@nerdwallet/nerdwallet-plus-components';

import AppDownloadPopover from '@components/app-download-popover';
import useIsMobile from '@lib/hooks/use-is-mobile';
import LocalStorage from '@lib/localStorage';
import { SegmentEventViewed, useAnalytics } from '@lib/segment';
import { getMobileOperatingSystem } from '@lib/utils';

import qrCode from './qr-code.svg';
import BannerCloseIcon from './banner-close-icon';
import WarningIcon from './warning.svg';

const WEB_DEPRECATION_BANNER_KEY = 'hasDismissedWebDeprecation';
const WEB_DEPRECATION_SUB_KEY = 'dismissed';
export const DOWNLOAD_APP_LINK =
  'https://click.nerdwallet.com/3687710914/kwg0nlea';
const WEB_DEPRECATION_FAQ = 'https://www.nerdwallet.com/p/web-deprecation-faq';

const WebDeprecationBanner = () => {
  const { segmentTrack } = useAnalytics();

  //  Determining if it is mobile device
  const { isDesktop } = useIsMobile();
  const isMobile = useMemo(
    () => !isDesktop && !!getMobileOperatingSystem(),
    [isDesktop],
  );

  const [qrCodeAnchorEl, setQRCodeAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [isDismissed, setIsDismissed] = useState(true);

  useEffect(() => {
    setIsDismissed(
      LocalStorage.get(WEB_DEPRECATION_BANNER_KEY, WEB_DEPRECATION_SUB_KEY) ===
        'true',
    );
  }, []);

  //  Watching mobile changes to hide QR code
  useEffect(() => {
    if (!isMobile) return;
    setQRCodeAnchorEl(null);
  }, [isMobile]);

  const onBannerClose = () => {
    segmentTrack('Web Deprecation Banner Dismissed');
    setIsDismissed(true);
    LocalStorage.add(
      WEB_DEPRECATION_BANNER_KEY,
      WEB_DEPRECATION_SUB_KEY,
      'true',
    );
  };

  const onFaqClicked = () => {
    segmentTrack('Web Deprecation Banner FAQ Clicked');
    window.location.assign(WEB_DEPRECATION_FAQ);
  };

  const onDownloadApp = (event: React.MouseEvent<HTMLElement>) => {
    segmentTrack('Web Deprecation Banner CTA Clicked');
    if (isMobile) return window.open(DOWNLOAD_APP_LINK, '_blank');
    setQRCodeAnchorEl(event?.currentTarget);
  };

  return (
    <When condition={!isDismissed}>
      <SegmentEventViewed eventName="Web Deprecation Banner Viewed">
        <Box display="flex" flexDirection="row" paddingY="3" width="100">
          <CenteredContainer>
            <Box
              alignItems={{ desktop: 'center', mobile: 'flex-start' }}
              backgroundColor="yellow-lightest"
              display="flex"
              flexDirection={{ desktop: 'row', mobile: 'column' }}
              gridGap={{ desktop: '4', mobile: '2' }}
              justifyContent="center"
              paddingX="4"
              paddingY="3"
              width="100"
            >
              <BannerCloseIcon onClick={onBannerClose} />
              <Box
                alignItems="flex-start"
                display="flex"
                flexDirection={{ desktop: 'row', mobile: 'column' }}
                gridGap="2"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  width={{ desktop: 'auto', mobile: '100' }}
                >
                  <Image alt="" src={WarningIcon} width={18} height={24} />
                  <BannerCloseIcon isMobile onClick={onBannerClose} />
                </Box>
                <Box display="flex" flexDirection="column" gridGap="1">
                  <Text bold>
                    Upcoming changes to your dashboard experience
                  </Text>
                  <Text size="small">
                    We&apos;re simplifying your dashboard to focus on enhancing
                    the mobile app. While some features will no longer be here,
                    you can access them and more in our mobile app.{' '}
                    <Link onClick={onFaqClicked}>What&apos;s changing?</Link>
                  </Text>
                </Box>
              </Box>
              <Box width={{ desktop: '25', mobile: '60' }}>
                <Button block onClick={onDownloadApp}>
                  {isMobile ? 'OPEN' : 'GET THE APP'}
                </Button>
              </Box>
            </Box>
          </CenteredContainer>
        </Box>
      </SegmentEventViewed>

      <AppDownloadPopover
        anchorEl={qrCodeAnchorEl}
        onClose={() => setQRCodeAnchorEl(null)}
        open={!!qrCodeAnchorEl}
        qrCode={qrCode}
      />
    </When>
  );
};

export default WebDeprecationBanner;
